import React, { useEffect, useState, useRef } from "react";
import {
  Container, Typography, Box, FormControl, InputLabel, Select,
  MenuItem, Paper, Slider, FormControlLabel, Switch, Button, useMediaQuery
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../components/AuthHeader";

function Ex2ViewerPage({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const [sentences, setSentences] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [loopAll, setLoopAll] = useState(false);
  const [autoNext, setAutoNext] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [autoNextTimer, setAutoNextTimer] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);

  const audioRefs = useRef({});
  const sentenceRefs = useRef([]);
  const isPlayingRef = useRef(false);
  
  // 音声ファイルの存在確認状態を管理
  const [audioFileExists, setAudioFileExists] = useState({});

  // 音声再生ログ関連
  const [currentPlayLog, setCurrentPlayLog] = useState(null);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/ex2_viewer' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 音声再生ログ開始
  const startAudioPlayLog = async (sentence, audioFileName) => {
    // db_user_idが無い場合はログアウトしてログイン画面へリダイレクト
    if (!user?.db_user_id) {
      console.warn('⚠️ db_user_idが無いため、ログアウトしてログイン画面へリダイレクトします。');
      logout();
      return;
    }

    try {
      // audio_filenameからtranscribed_ex2s.idを取得
      const idResponse = await fetch(`${API_BASE}/transcribed_ex2_id_by_audio/${audioFileName}`);
      const idData = await idResponse.json();
      const transcribedId = idData.id;

      const logData = {
        session_id: String(user.db_user_id),
        content_type: 'ex2',
        content_id: transcribedId || sentence.id, // transcribed_ex2s.idを優先、なければsentence.id
        audio_file_name: audioFileName,
        is_video: 0, // 音声の場合は0
        play_start_time: new Date().toISOString(),
        playback_rate: playbackRate
      };

      const response = await fetch(`${API_BASE}/audio_play_logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      });

      const data = await response.json();
      if (data.success) {
        setCurrentPlayLog({
          log_id: data.log_id,
          start_time: new Date(),
          ...logData
        });
        console.log('★音声再生ログ開始:', data.log_id);
      }
    } catch (error) {
      console.error('★音声ログ開始エラー:', error);
    }
  };

  // 音声再生ログ完了
  const completeCurrentPlayLog = async () => {
    if (!currentPlayLog) return;

    try {
      const endTime = new Date();
      const durationSeconds = (endTime - currentPlayLog.start_time) / 1000;

      const updateData = {
        ...currentPlayLog,
        play_end_time: endTime.toISOString(),
        duration_seconds: durationSeconds
      };

      await fetch(`${API_BASE}/audio_play_logs/${currentPlayLog.log_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      console.log('★音声再生ログ完了:', durationSeconds, '秒');
      setCurrentPlayLog(null);
    } catch (error) {
      console.error('★音声ログ完了エラー:', error);
    }
  };

  useEffect(() => {
    fetch(`${API_BASE}/ex2_transcription_weeks`)
      .then((res) => res.json())
      .then((data) => {
        const filtered = Array.isArray(data)
          ? data.map((w) => w.week_num)
          : [];
        const reversed = [...filtered].reverse();
        setWeekOptions(reversed);
        if (reversed.length > 0 && !selectedWeek) {
          setSelectedWeek(reversed[0]);
        }
      })
      .catch((err) => console.error("week fetch error:", err));
  }, []);

  useEffect(() => {
    if (!selectedWeek) return;

    fetch(`${API_BASE}/transcribed_ex2s_by_week/${selectedWeek}`)
      .then((res) => res.json())
      .then((data) => {
        const sentenceData = Array.isArray(data) ? data : [];
        setSentences(sentenceData);
      })
      .catch((err) => console.error("API fetch error:", err));
  }, [selectedWeek]);

  useEffect(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.playbackRate = playbackRate;
    });
  }, [playbackRate, sentences]);

  useEffect(() => {
    if (currentIndex !== null && sentenceRefs.current[currentIndex]) {
      sentenceRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentIndex]);

  // 音声ファイルの存在確認
  const checkAudioFileExists = async (filename) => {
    try {
      const response = await fetch(`${AUDIO_BASE}/audio_clipped/${filename}`, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // sentencesが更新されたときに音声ファイルの存在確認を実行
  useEffect(() => {
    const checkAllAudioFiles = async () => {
      const existsMap = {};
      for (const item of sentences) {
        if (Array.isArray(item.audio_files)) {
          for (const file of item.audio_files) {
            const exists = await checkAudioFileExists(file);
            existsMap[file] = exists;
          }
        }
      }
      setAudioFileExists(existsMap);
    };

    if (sentences.length > 0) {
      checkAllAudioFiles();
    }
  }, [sentences, AUDIO_BASE]);

  const stopAllAudio = async () => {
    // 現在の再生ログを完了
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }

    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    if (autoNextTimer) {
      clearTimeout(autoNextTimer);
      setAutoNextTimer(null);
    }
    isPlayingRef.current = false;
    setCurrentIndex(null);
    setPlayingKey(null);
  };

  const handleEnded = async () => {
    // 音声終了時の処理
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }

    if (!isPlayingRef.current || !playingKey) return;
    const [indexStr, audioIndexStr] = playingKey.split("_");
    const index = parseInt(indexStr);
    const audioIndex = parseInt(audioIndexStr);
    const currentItem = sentences[index];
    const nextAudioIndex = audioIndex + 1;

    if (Array.isArray(currentItem.audio_files) && nextAudioIndex < currentItem.audio_files.length) {
      if (autoNext) {
        const nextKey = `${index}_${nextAudioIndex}`;
        const nextAudio = audioRefs.current[nextKey];
        if (nextAudio) {
          stopAllAudio();
          nextAudio.play();
          setPlayingKey(nextKey);
          setCurrentIndex(index);
          isPlayingRef.current = true;
          return;
        }
      } else if (loopAll) {
        const currentKey = `${index}_${audioIndex}`;
        const currentAudio = audioRefs.current[currentKey];
        if (currentAudio) {
          currentAudio.currentTime = 0;
          currentAudio.play();
          return;
        }
      } else {
        stopAllAudio();
        return;
      }
    }

    if (autoNext) {
      let nextIndex = index + 1;
      if (nextIndex >= sentences.length) {
        if (loopAll) {
          nextIndex = 0;
        } else {
          stopAllAudio();
          return;
        }
      }
      const timer = setTimeout(() => {
        const nextKey = `${nextIndex}_0`;
        const nextAudio = audioRefs.current[nextKey];
        if (nextAudio) {
          stopAllAudio();
          nextAudio.play();
          setPlayingKey(nextKey);
          setCurrentIndex(nextIndex);
          isPlayingRef.current = true;
        }
      }, 1000);
      setAutoNextTimer(timer);
    } else if (loopAll) {
      const currentKey = `${index}_${audioIndex}`;
      const currentAudio = audioRefs.current[currentKey];
      if (currentAudio) {
        currentAudio.currentTime = 0;
        currentAudio.play();
      }
    } else {
      stopAllAudio();
    }
  };

  const handlePlay = async (index, audioIndex = 0) => {
    await stopAllAudio();
    
    const key = `${index}_${audioIndex}`;
    const audio = audioRefs.current[key];
    const currentSentence = sentences[index];
    
    if (audio && currentSentence) {
      audio.play();
      isPlayingRef.current = true;
      setCurrentIndex(index);
      setPlayingKey(key);
      
      // 音声ファイル名を取得
      let audioFileName = null;
      if (Array.isArray(currentSentence.audio_files) && currentSentence.audio_files[audioIndex]) {
        audioFileName = currentSentence.audio_files[audioIndex];
      }
      
      // 音声再生ログ開始
      if (audioFileName) {
        await startAudioPlayLog(currentSentence, audioFileName);
      }
    }
  };

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null; // リダイレクト中は何も表示しない
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: 24 }}>

      <Box sx={{ marginBottom: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="week-label">週番号を選択</InputLabel>
          <Select
            labelId="week-label"
            value={selectedWeek}
            label="週番号を選択"
            onChange={(e) => setSelectedWeek(e.target.value)}
          >
            {weekOptions.map((week, i) => (
              <MenuItem key={i} value={week}>{week}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {sentences.map((item, index) => (
          <Paper
            key={item.id || index}
            ref={(el) => (sentenceRefs.current[index] = el)}
            sx={{ padding: 2, backgroundColor: currentIndex === index ? '#e3f2fd' : 'inherit' }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              【{item.sentence_index ?? index + 1}】
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {Array.isArray(item.audio_files) && item.audio_files.map((file, i) => {
                const fileExists = audioFileExists[file];
                return (
                <React.Fragment key={`${index}_${i}`}>
                  {playingKey === `${index}_${i}` ? (
                    <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1 }}>
                      停止
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      onClick={() => handlePlay(index, i)} 
                      sx={{ mr: 1 }}
                      disabled={false}
                      title={fileExists === false ? "音声ファイルが見つかりません" : ""}
                    >
                      再生
                    </Button>
                  )}
                  <audio
                    style={{ display: "none" }}
                    ref={(el) => (audioRefs.current[`${index}_${i}`] = el)}
                    onEnded={handleEnded}
                  >
                    <source src={`${AUDIO_BASE}/audio_clipped/${file}`} type="audio/mpeg" />
                  </audio>
                </React.Fragment>
                );
              })}
            </Box>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          padding: 2,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Box minWidth={200}>
          <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
          <Slider
            value={playbackRate}
            onChange={(e, val) => setPlaybackRate(val)}
            min={0.5}
            max={1.5}
            step={0.1}
            valueLabelDisplay="auto"
            marks={[{ value: 0.5, label: "0.5x" }, { value: 1.0, label: "1.0x" }, { value: 1.5, label: "1.5x" }]}
          />
        </Box>
        <Box display="flex" flexDirection="row" gap={4}>
          <FormControlLabel
            control={<Switch checked={loopAll} onChange={(e) => setLoopAll(e.target.checked)} />}
            label="ループ再生"
          />
          <FormControlLabel
            control={<Switch checked={autoNext} onChange={(e) => setAutoNext(e.target.checked)} />}
            label="連続再生"
          />
        </Box>
      </Box>
      </Container>
    </>
  );
}

export default Ex2ViewerPage;